
import { useEffect, useState, useRef } from 'react';
import {
    Box,
    Stack,
    Paper,
    Typography,
    ButtonBase,
    FormControlLabel,
    Button,
    Divider, Checkbox,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useCommonStyles } from '../../styles/GenericStyle';
import ScanInput from '../ScanInput';
import ScanButton from '../spLot/ScanIcon';
import { black } from "../../colors";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GenericConstant } from '../../utils/GenericConstant';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import packageplusIcon from '/icons/packageplus_icon.png';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AddMaterial from './addMaterial';
import ReportNoSeedHandheld from './ReportNoSeedHandheld';
import { useGetDrillDownSPLotById, useGetSPLotWorkflowDetails } from '../../api/vegApl';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useGlobalState } from '../../contexts/GlobalState';
export type ProgressPopop = {

    setIsShowSummaryCards: (show: boolean) => void,

}

export default function ProgressPage({ setIsShowSummaryCards }: ProgressPopop) {

    const classes = useCommonStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [scannedValue, setScannedValue] = useState("");
    const manualEdit = true;
    const errorText = "";
    const [isShowScanner, setShowScanner] = useState(false);
    const [isShowAddMaterialScan, setShowAddMaterialScan] = useState(false);
    const [isShowAddMaterial, setShowAddMaterial] = useState(false);
    const [isShowReportNoSeed, setShowReportNoSeed] = useState(false);
    const [addMaterialSpLotInput, setAddMaterialSpLotInput] = useState('');
    const [selectedAddMaterialSpLotId, setSelectedAddMaterialSpLotId] = useState('');
    const [isAddMatValidating, setIsAddMatValidating] = useState(false);
    const [validationTrigger, setValidationTrigger] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { state: { mobileHome } } = useGlobalState();

    // Reset all sub-views when user taps the home icon
    useEffect(() => {
        if (mobileHome) {
            setShowScanner(false);
            setShowAddMaterialScan(false);
            setShowAddMaterial(false);
            setShowReportNoSeed(false);
            setScannedValue('');
            setAddMaterialSpLotInput('');
            setSelectedAddMaterialSpLotId('');
            setValidSpotList([]);
        }
    }, [mobileHome]);

    const { mutate: getSPLotWorkflowDetails } = useGetSPLotWorkflowDetails({
        mutation: {
            onSuccess: () => {
                // After workflow details are fetched, call drill down
                const splotId = addMaterialSpLotInput.trim();
                getDrillDownData({ splot_id: splotId });
            },
            onError: (error: any) => {
                setIsAddMatValidating(false);
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error fetching SPLot details';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const { mutate: getScanWorkflowDetails, isLoading: isScanWorkflowLoading } = useGetSPLotWorkflowDetails({
        mutation: {
            onSuccess: (response: any) => {
                const workflowData = response?.data;

                if (!Array.isArray(workflowData) || workflowData.length === 0) {
                    showSnackbar({ message: 'SPLot empty', severity: 'error' });
                    return;
                }

                const hasEmptyWorkflow = workflowData.some(
                    (item: any) => !Array.isArray(item?.Workflow_Steps) || item.Workflow_Steps.length === 0
                );

                if (hasEmptyWorkflow) {
                    showSnackbar({ message: 'SPLot empty', severity: 'error' });
                    return;
                }

                const requestedIds = pendingScanIdsRef.current;
                if (!Array.isArray(requestedIds) || requestedIds.length === 0) {
                    showSnackbar({ message: 'Please scan SP Lot', severity: 'warning' });
                    return;
                }

                navigate('/processing-workflow', { state: { spLotIds: requestedIds, originComponent: 'scan' } });
            },
            onError: (error: any) => {
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error fetching SP Lot workflow details';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const { mutate: getDrillDownData } = useGetDrillDownSPLotById({
        mutation: {
            onSuccess: (data: any) => {
                setIsAddMatValidating(false);
                const materials = data?.data?.materials ?? [];
                const hasBlockingStatus = materials.length > 0 && materials.some(
                    (m: any) => m.material_status?.toLowerCase() === 'processing' ||
                                m.material_status?.toLowerCase() === 'registered'
                );
                if (hasBlockingStatus) {
                    showSnackbar({
                        message: 'Cannot add materials: this SP Lot has materials currently in Processing or Registered status.',
                        severity: 'error'
                    });
                } else {
                    setSelectedAddMaterialSpLotId(addMaterialSpLotInput.trim());
                    setShowAddMaterialScan(false);
                    setShowAddMaterial(true);
                }
            },
            onError: () => {
                setIsAddMatValidating(false);
                const enteredSplotId = addMaterialSpLotInput.trim();
                setSelectedAddMaterialSpLotId(enteredSplotId);
                setShowAddMaterialScan(false);
                setShowAddMaterial(true);
            },
        },
    });
    const [validSpotList, setValidSpotList] = useState<string[]>([]);
    const [isMultipleSPLotsChecked, setIsMultipleSPLotsChecked] = useState(true);
    const scanInputRef = useRef<HTMLInputElement>(null);
    const pendingScanIdsRef = useRef<string[]>([]);
    const [hasInputValue, setHasInputValue] = useState(false);

    const handleRemoveSpot = (spot: string) => {
        setValidSpotList((prev) => prev.filter((s) => s !== spot));
    };

    const handleAddMaterialSpLotScan = () => {
        if (addMaterialSpLotInput.trim()) {
            setIsAddMatValidating(true);
            getSPLotWorkflowDetails({
                data: { splot_ids: [addMaterialSpLotInput.trim()] }
            });
        }
    };

    const handleScanDone = (splotIds: string[]) => {
        const normalizedIds = (splotIds || []).map((id) => id?.trim()).filter(Boolean);
        if (normalizedIds.length === 0) {
            showSnackbar({ message: 'Please scan SP Lot', severity: 'warning' });
            return;
        }

        pendingScanIdsRef.current = normalizedIds;
        getScanWorkflowDetails({ data: { splot_ids: normalizedIds } });
    };

    const handleForwardArrowClick = () => {
        if (isMultipleSPLotsChecked) {
            setValidationTrigger(prev => !prev);
            return;
        }

        const currentValue = scanInputRef.current?.value || scannedValue;
        const singleIds = String(currentValue)
            .split(',')
            .map(id => id.trim())
            .filter(Boolean);

        handleScanDone(singleIds);
    };

    useEffect(() => {
        setIsShowSummaryCards(isShowScanner || isShowAddMaterialScan || isShowAddMaterial || isShowReportNoSeed);
    }, [isShowScanner, isShowAddMaterialScan, isShowAddMaterial, isShowReportNoSeed]);

    useEffect(() => {
        const locationState = location.state as { openScanner?: boolean } | null;
        if (locationState?.openScanner) {
            setShowScanner(true);
        }
    }, [location.state]);

    return (
        <Box sx={{ width: "100%", px: 2 }}>
            <FullScreenActionLoader open={isScanWorkflowLoading || isAddMatValidating} />
            {!isShowScanner && !isShowAddMaterialScan && !isShowAddMaterial && !isShowReportNoSeed && <Stack sx={{ width: "100%", mt: '24px' }}>
                <Divider
                    sx={{
                        borderTop: '0.5px solid #989898',
                        mb: '24px',
                        mx: -2,
                        width: 'auto',
                    }}
                />
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '16px',
                        overflow: 'hidden',
                        width: '100%',
                        opacity: 1,
                        background: '#FFFFFF',
                        border: '1px solid #C3C3C3',
                        boxShadow: '0px 1px 2px -1px #0000001A',
                    }}
                >
                    <ButtonBase
                        disableRipple
                        onClick={() => { setShowScanner(true) }}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            minHeight: 76,
                            py: '12px',
                            textAlign: 'left',
                            pl: '16px',
                            pr: '16px',
                            backgroundColor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.25,
                            opacity: 1,
                            outline: 'none',
                            '&:focus': { outline: 'none' },
                            '&:active': { outline: 'none' },
                            '&:hover': { outline: 'none' },
                        }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                backgroundColor: '#E7ECFF',
                                border: '1px solid #C9D5FF',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#1E5BFF',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                flexShrink: 0,
                            }}
                        >
                            <QrCode2RoundedIcon sx={{ fontSize: 28.8 }} />
                        </Box>


                        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                                component="span"
                                className={classes.labelNoto16Bold500_hh}
                            >
                                Process SP Lot
                            </Typography>
                            <Typography component="span" className={classes.labelNoto14Bold400_hh} sx={{ color: 'text.secondary', mt: 0.25, overflowWrap: 'anywhere', lineHeight: 1.3 }}>
                                Scan SP Lot to start or continue workflow
                            </Typography>
                        </Stack>


                        <Box sx={{ color: 'rgba(0,0,0,0.5)', pr: 0.5 }}>
                            <ChevronRightRoundedIcon sx={{ fontSize: 24 }} />
                        </Box>
                    </ButtonBase>
                </Paper>

                {/* Add Materials card */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '16px',
                        overflow: 'hidden',
                        width: '100%',
                        mt: 2,
                        opacity: 1,
                        background: '#FFFFFF',
                        border: '1px solid #C3C3C3',
                        boxShadow: '0px 1px 2px -1px #0000001A',
                    }}
                >
                    <ButtonBase
                        disableRipple
                        onClick={() => {
                            setAddMaterialSpLotInput('');
                            setSelectedAddMaterialSpLotId('');
                            setIsAddMatValidating(false);
                            setShowAddMaterialScan(true);
                        }}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            minHeight: 76,
                            py: '12px',
                            textAlign: 'left',
                            pl: '16px',
                            pr: '16px',
                            backgroundColor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.25,
                            opacity: 1,
                            outline: 'none',
                            '&:focus': { outline: 'none' },
                            '&:active': { outline: 'none' },
                            '&:hover': { outline: 'none' },
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                backgroundColor: '#F3F4F6',
                                border: '1px solid #DEDEDE',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#374151',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                flexShrink: 0,
                            }}
                        >
                            <Box
                                component="img"
                                src={packageplusIcon}
                                alt="Add Materials"
                                sx={{ width: 28.8, height: 28.8, display: 'block' }}
                            />
                        </Box>

                        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography component="span" className={classes.labelNoto16Bold500_hh}>
                                Add Materials
                            </Typography>
                            <Typography component="span" className={classes.labelNoto14Bold400_hh} sx={{ color: 'text.secondary', mt: 0.25, overflowWrap: 'anywhere', lineHeight: 1.3 }}>
                                Scan SP Lot to add items
                            </Typography>
                        </Stack>

                        <Box sx={{ color: 'rgba(0,0,0,0.5)', pr: 0.5 }}>
                            <ChevronRightRoundedIcon sx={{ fontSize: 24 }} />
                        </Box>
                    </ButtonBase>
                </Paper>

                {/* Report No Seed card */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '16px',
                        overflow: 'hidden',
                        width: '100%',
                        mt: 2,
                        mb: 6,
                        opacity: 1,
                        background: '#FFFFFF',
                        border: '1px solid #C3C3C3',
                        boxShadow: '0px 1px 2px -1px #0000001A',
                    }}
                >
                    <ButtonBase
                        disableRipple
                        onClick={() => setShowReportNoSeed(true)}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            minHeight: 76,
                            py: '12px',
                            textAlign: 'left',
                            pl: '16px',
                            pr: '16px',
                            backgroundColor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.25,
                            opacity: 1,
                            outline: 'none',
                            '&:focus': { outline: 'none' },
                            '&:active': { outline: 'none' },
                            '&:hover': { outline: 'none' },
                        }}
                    >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                backgroundColor: '#FFEDD4',
                                border: '1px solid #FED7AA',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#EA580C',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                                flexShrink: 0,
                            }}
                        >
                            <WarningAmberOutlinedIcon sx={{ color: '#EA580C', fontSize: 24 }} />
                        </Box>

                        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography component="span" className={classes.labelNoto16Bold500_hh}>
                                Report No Seed
                            </Typography>
                            <Typography component="span" className={classes.labelNoto14Bold400_hh} sx={{ color: 'text.secondary', mt: 0.25, overflowWrap: 'anywhere', lineHeight: 1.3 }}>
                                Scan material to report no seed
                            </Typography>
                        </Stack>

                        <Box sx={{ color: 'rgba(0,0,0,0.5)', pr: 0.5 }}>
                            <ChevronRightRoundedIcon sx={{ fontSize: 24 }} />
                        </Box>
                    </ButtonBase>
                </Paper>

            </Stack>
            }

            {isShowAddMaterialScan && (
                <Box width="100%" display="flex" flexDirection="column" alignItems="center" sx={{ minHeight: 'calc(100vh - 140px)' }}>
                    <Divider />
                    <Box display="flex" width="100%" alignItems="center" justifyContent="flex-start">
                        <ArrowBackIosIcon
                            sx={{ verticalAlign: 'middle', cursor: 'pointer', fontSize: 28 }}
                            onClick={() => setShowAddMaterialScan(false)}
                        />
                        <Typography component="div" sx={{ py: 1.25, px: 1.5 }} className={classes.labelNoto20Bold500_hh}>Scan SP Lot</Typography>
                    </Box>
                    <Divider sx={{ borderColor: 'grey.400', borderBottomWidth: '1px', width: '100%' }} />
                    <Box sx={{ width: '100%', pt: '26px', px: 1, pb: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <ScanButton />
                        <Stack direction="row" spacing={1} sx={{ width: '100%', mt: '24px', alignItems: 'flex-end' }}>
                            <ScanInput
                                softFocusOnMount
                                submitOnScan={false}
                                scanPickupEnabled={false}
                                defaultValue={addMaterialSpLotInput}
                                onChange={(val) => setAddMaterialSpLotInput(val)}
                                onScan={(val) => {
                                    setAddMaterialSpLotInput(val);
                                }}
                                onEnterPress={handleAddMaterialSpLotScan}
                                manualEdit
                                error={false}
                                label="SP Lot Identifier"
                                errorText=""
                                placeholder="Scan or Enter SP Lot ID"
                            />
                            {addMaterialSpLotInput.trim() && (
                            <Button
                                variant="contained"
                                className={classes.blueButton_hh}
                                disabled={isAddMatValidating}
                                sx={{
                                    height: 40, width: 40, minWidth: 40,
                                    '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0, padding: 0 },
                                }}
                                startIcon={<ArrowForwardIcon sx={{ color: 'white', fontSize: 26 }} />}
                                onClick={handleAddMaterialSpLotScan}
                            />
                            )}
                        </Stack>
                    </Box>
                </Box>
            )}

            {isShowAddMaterial && (
                <AddMaterial
                    spLotId={selectedAddMaterialSpLotId}
                    onBack={() => {
                        setShowAddMaterial(false);
                        setShowAddMaterialScan(true);
                    }}
                    onSaveToHome={() => {
                        setShowAddMaterial(false);
                        setShowAddMaterialScan(false);
                        setAddMaterialSpLotInput('');
                        setSelectedAddMaterialSpLotId('');
                    }}
                />
            )}

            {isShowReportNoSeed && (
                <ReportNoSeedHandheld
                    onBack={() => setShowReportNoSeed(false)}
                    onDone={() => setShowReportNoSeed(false)}
                />
            )}

            {isShowScanner && <Box width="100%" display={"flex"} flexDirection={"column"} alignItems={"center"} sx={{ minHeight: 'calc(100vh - 140px)' }}>
                <Divider />
                <Box display={"flex"} width="100%" alignItems={"center"} justifyContent={"flex-start"}>
                    <ArrowBackIosIcon sx={{
                        verticalAlign: "middle",
                        cursor: "pointer",
                        fontSize: 28,
                    }} onClick={() => { setShowScanner(false) }} />

                    <Typography component="div" sx={{ py: 1.25, px: 1.5 }} className={classes.labelNoto20Bold500_hh}>Scan SPLot</Typography>
                </Box>


                <Divider
                    sx={{
                        borderColor: "grey.400",
                        borderBottomWidth: "1px",
                        position: 'relative',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        width: "100%"
                    }}
                />

                <Box sx={{ width: "100%", pt: '26px', px: 1, pb: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <ScanButton />
                    <Stack direction="row" spacing={1} sx={{ width: "100%", mt: '24px', alignItems: 'flex-end' }}>
                        <ScanInput
                            ref={scanInputRef}
                            softFocusOnMount
                            submitOnScan
                            scanPickupEnabled={!manualEdit}
                            defaultValue={scannedValue}
                            onScan={(val) => setScannedValue(val)}
                            manualEdit={manualEdit}
                            error={errorText?.length ? true : false}
                            label={'Material Identifier'}
                            errorText={errorText}
                            triggerValidation={validationTrigger}
                            onValidationSuccess={(data) => { setValidSpotList(data) }}
                            isMultipleSPLotsChecked={isMultipleSPLotsChecked}
                            validSpotList={validSpotList}
                            onDone={handleScanDone}
                            onInputChange={(hasValue) => setHasInputValue(hasValue)}
                            placeholder="Scan or Enter SP Lot ID"
                        />
                        {(hasInputValue) && <Button
                            variant="contained"
                            className={classes.blueButton_hh}
                            disableRipple disableFocusRipple
                            disabled={isScanWorkflowLoading}
                            sx={{
                                minHeight: 40, width: 40, minWidth: 40, marginTop: 3,
                                '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                    margin: 0,
                                    padding: 0,
                                },
                            }}
                            startIcon={<ArrowForwardIcon sx={{ color: "white", fontSize: 26 }} />}
                            onClick={handleForwardArrowClick}
                        ></Button>}
                    </Stack>
                    <Stack direction="column" sx={{ width: "100%", mt: '20px' }}>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isMultipleSPLotsChecked}
                                    sx={GenericConstant.CHECKBOXSTYLE}
                                    checkedIcon={<Box component="img" alt="" src={"icons/CheckBox.svg"} />}
                                    onChange={(e) => {
                                        setIsMultipleSPLotsChecked(e.target.checked);
                                        setTimeout(() => {
                                            scanInputRef.current?.focus();
                                        }, 0);
                                    }}
                                />
                            }
                            label="Scan multiple SP Lots"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontWeight: 500,
                                    fontSize: 20.8
                                },
                            }}
                            className={classes.labelNoto16Bold500_hh}
                        />
                    </Stack>
                    {isMultipleSPLotsChecked && <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Stack direction="column" sx={{ width: "100%", mt: 1 }}>
                            <Typography component="div" sx={{ mt: 3 }} className={classes.labelNoto20Bold500_hh}>SP Lots ({validSpotList.length})</Typography>
                            <Divider
                                sx={{
                                    borderColor: "grey.400",
                                    borderBottomWidth: "1px",
                                    position: 'relative',
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    width: "100%"
                                }}
                            />
                        </Stack>

                        <Stack width="100%" display={"flex"} spacing={1} flexDirection={"column"} alignItems={"center"} sx={{ mt: 2, flex: 1 }}>
                            {validSpotList.map((spot) => (
                                <Box key={spot} sx={{ display: "flex", flexDirection: "row", backgroundColor: "#F9FAFB", justifyContent: "space-between", alignItems: "center", gap: 2, padding: "10px", width: "100%", border: "0.5px solid #E5E7EB", borderRadius: "8px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <img
                                            src={"icons/SP_Lots_Icon_black.svg"}
                                            alt="Save"
                                            width={19.2}
                                            height={19.2}
                                            style={{ display: "block" }}
                                        />
                                        <Typography component="span" sx={{ ml: 3 }} className={classes.labelNoto14Bold400_hh}>{spot}</Typography>
                                    </Box>
                                    <IconButton aria-label="close" onClick={() => handleRemoveSpot(spot)}>
                                        <CloseIcon sx={{ color: black, fontSize: 19 }} />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button
                                variant="contained"
                                disableRipple disableFocusRipple
                                disabled={validSpotList.length === 0 || isScanWorkflowLoading}
                                sx={{
                                    minHeight: 48,
                                    height: 'auto',
                                    width: "100%",
                                    mt: 'auto',
                                    borderRadius: "12px",
                                    opacity: 1,
                                    gap: "6px",
                                    py: "12px",
                                    px: "16px",
                                    textAlign: 'center',
                                    lineHeight: 1.2,
                                    background: "#094BC7",
                                    boxShadow: "0px 4px 6px -4px #0000001A, 0px 10px 15px -3px #0000001A",
                                    '&:focus': { outline: "none" },
                                    '&:active': { outline: "none" },
                                    '&:hover': { outline: "none" }
                                }}
                                startIcon={<CheckCircleIcon sx={{ color: "white", fontSize: 26 }} />}
                                onClick={() => handleScanDone(validSpotList)}
                            >
                                {isScanWorkflowLoading ? 'Validating...' : 'Done'}
                            </Button>
                        </Stack>
                    </Box>}
                </Box>
            </Box>
            }

        </Box>
    );
}
